import { haveAppFunction, downloadFile as appDownloadFile, openFile as appOpenFile } from "./app-helper";
import {
	getFileExtention,
	getFileNameValidationResult,
} from "../pages/documents/helpers/get-file-name-validation";

interface MsSaveOrOpenBlobFunction {
	msSaveOrOpenBlob?: (data: string | Blob | File, fileName: string) => boolean;
}

export function downloadFile(data: Blob | File | string, fileName: string): void {
	if (!data) return;

	if (haveAppFunction("downloadFile")) {
		if (typeof data === "string") {
			appDownloadFile({
				fileName,
				base64String: data,
			});
		}

		switch (data.constructor) {
			case File:
			case Blob:
				downloadBlob(data as File | Blob);
				break;
			default:
				console.error("Unsupported format!");
				break;
		}
	} else {
		const msSaveOrOpenBlob = (<MsSaveOrOpenBlobFunction>(<unknown>window.navigator)).msSaveOrOpenBlob;
		if (window.navigator && msSaveOrOpenBlob) {
			msSaveOrOpenBlob(data, decodeURIComponent(fileName));
		} else {
			const link = document.createElement("a");
			link.href = window.URL.createObjectURL(data as Blob);

			try {
				link.download = decodeURIComponent(fileName); //Should "force" download. Supported by Chrome 44 and FF 39, not IE 11 or Safari 8.
			} catch {
				link.download = fileName; //Should "force" download. Supported by Chrome 44 and FF 39, not IE 11 or Safari 8.
			}

			link.click();
		}
	}

	function downloadBlob(data: Blob | File) {
		const reader = new FileReader();
		reader.readAsDataURL(data);
		reader.onloadend = function () {
			const dataString = reader.result;
			const base64String = typeof dataString === "string" ? dataString?.toString()?.split(",")[1] : "";

			if (base64String) {
				appDownloadFile({
					fileName,
					base64String: base64String,
				});
			}
		};
	}
}

export const openFile = (data: Blob | File | string, fileName: string) => {
	if (!data) return;

	if (haveAppFunction("openFile")) {
		if (typeof data === "string") {
			appOpenFile({
				fileName,
				base64String: data,
			});
		}

		switch (data.constructor) {
			case File:
			case Blob:
				openBlob(data as File | Blob);
				break;
			default:
				console.error("Unsupported format!");
				break;
		}
	} else {
		const url = window.URL.createObjectURL(data as File);
		window.open(url);
	}

	function openBlob(data: Blob | File) {
		const reader = new FileReader();
		reader.readAsDataURL(data);
		reader.onloadend = function () {
			const dataString = reader.result;
			const base64String = typeof dataString === "string" ? dataString?.toString()?.split(",")[1] : "";

			if (base64String) {
				appOpenFile({
					fileName,
					base64String: base64String,
				});
			}
		};
	}
};

export function base64ToFile(base64String: string, fileName: string, mimeType: string) {
	const byteCharacters = atob(base64String);

	const byteNumbers = new Array(byteCharacters.length);

	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}

	const byteArray = new Uint8Array(byteNumbers);

	const blob = new Blob([byteArray], { type: mimeType });
	const file = new File([blob], fileName, { type: mimeType });

	return file;
}

export function getMimeType(fileName: string): string {
	const extension = fileName.split(".").pop()?.toLowerCase();
	switch (extension) {
		case "pdf":
			return "application/pdf";
		case "jpg":
		case "jpeg":
			return "image/jpeg";
		case "png":
			return "image/png";
		case "gif":
			return "image/gif";
		// Add more cases as needed
		default:
			return "application/octet-stream"; // Default MIME type
	}
}

/**
 * Reads an image file and returns a base64 data URL with correct orientation.
 * Uses createImageBitmap to automatically handle EXIF orientation metadata,
 * ensuring portrait photos taken on phones display correctly.
 */
export async function readImageWithCorrectOrientation(file: File): Promise<string> {
	let bitmap: ImageBitmap | null = null;

	try {
		bitmap = await createImageBitmap(file);
		const canvas = document.createElement("canvas");
		canvas.width = bitmap.width;
		canvas.height = bitmap.height;

		const ctx = canvas.getContext("2d");
		if (!ctx) {
			throw new Error("Failed to get canvas context");
		}

		ctx.drawImage(bitmap, 0, 0);
		return canvas.toDataURL(file.type || "image/jpeg");
	} catch {
		// Fallback: read file directly without orientation correction
		return readFileAsBase64(file);
	} finally {
		if (bitmap) {
			bitmap.close();
		}
	}
}

export const readFileAsBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
};

export const validateFile = (file: File) => {
	let error = "";
	const variables: { [key: string]: string } = {};
	const allowedExtentions = ["pdf", "png", "jpg", "jpeg", "jifi", "pjpeg", "pjp"];

	const fileNameError = getFileNameValidationResult(file, allowedExtentions);
	if (!fileNameError) {
		error = "documents_fileNameInValid";
	}

	// Max 10 MB
	if (file.size > 10000000) {
		error = "documents_file_to_big";
	}

	const fileExtention = getFileExtention(file.name);
	if (!allowedExtentions.some((extention) => extention === fileExtention)) {
		error = "documents_file_extention_error";
		variables.extentions = allowedExtentions.join(", ");
	}

	if (error) {
		return { isValid: false, message: error, messageVariables: variables };
	}

	return { isValid: true, message: "success" };
};
